import {
  Component,
  computed,
  DestroyRef,
  inject,
  Input,
  OnInit,
  signal,
} from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';
import { UserDetailsModel } from '../../../../api/models/user-details-model';
import { AuthService } from '../../../../services/auth.service';
import { UserService } from '../../../../api/services/user.service';
import { ToastService } from '../../../../services/toast.service';
import { UserDetailsUpdateModel } from '../../../../api/models/user-details-update-model';
import { AccountModel } from '../../../../api/models/account-model';
import { DialogService } from '../../../../services/dialog.service';
import { CreateUserComponent } from '../create-user/create-user.component';
import { EmailVerificationService } from '../../../../api/services/email-verification-service';
import { EmailVerificationEmailType } from '../../../../enums/email-verification-email-type';
import { ThemeService } from '../../../../services/theme.service';
import { AccountStateService } from '../../../../services/account-state.service';
import { Role } from '../../../../enums/role';
import { AccountService } from '../../../../api/services/account.service';
import { DeactivateAccountDialogComponent } from './deactivate-account-dialog/deactivate-account-dialog.component';
import { Router } from '@angular/router';

type EmailChangeStep = 'update' | 'confirm' | 'saved';

@Component({
  selector: 'app-account-details',
  templateUrl: './account-details.component.html',
  standalone: false,
})
export class AccountDetailsComponent implements OnInit {
  @Input() accountDetails!: AccountModel;
  private readonly destroyRef = inject(DestroyRef);

  isEditingAccountDetails = signal(false);
  sendingVerificationEmail = signal(false);
  newPassword = signal('');
  userDetails = signal<UserDetailsModel | undefined>(undefined);
  emailChangeStep = signal<EmailChangeStep>('update');
  newEmail = signal('');
  pendingEmail = signal('');
  verificationCode = signal('');
  requestingEmailChange = signal(false);
  confirmingEmailChange = signal(false);
  resendingEmailChangeCode = signal(false);
  emailChangeError = signal<string | null>(null);
  emailChangeMessage = signal<string | null>(null);
  deactivatingAccount = signal(false);
  deactivationError = signal<string | null>(null);
  theme = this.themeService.getTheme();
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  isEmailChangeBusy = computed(
    () =>
      this.requestingEmailChange() ||
      this.confirmingEmailChange() ||
      this.resendingEmailChangeCode(),
  );
  canRequestEmailChange = computed(() => {
    return !this.isEmailChangeBusy();
  });
  canConfirmEmailChange = computed(() => !this.isEmailChangeBusy());
  canDeactivateAccount = computed(() => {
    const role = this.userDetails()?.role;

    return (
      role === Role.Owner ||
      role === Role.SuperAdmin ||
      this.authService.hasAnyRole([Role.Owner, Role.SuperAdmin])
    );
  });

  constructor(
    private authService: AuthService,
    private userService: UserService,
    private toastService: ToastService,
    private dialogService: DialogService,
    private emailVerificationService: EmailVerificationService,
    private themeService: ThemeService,
    private accountStateService: AccountStateService,
    private accountService: AccountService,
    private router: Router,
  ) {}

  ngOnInit() {
    const userGuid = this.authService.getUserGuid();

    if (!userGuid) {
      this.toastService.error('Could Not Get User Details');
      return;
    }

    this.userService
      .userGet(
        {
          accountGuid: this.accountDetails.guid,
          userGuid,
        },
        this.authService.getAuthorisationHeaders(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (userDetailsModel: UserDetailsModel) => {
          this.userDetails.set(userDetailsModel);
        },
        error: () => {
          this.toastService.error('Could Not Get User Details');
        },
      });
  }

  onClickEditAccountDetails() {
    const shouldSave = this.isEditingAccountDetails();
    this.isEditingAccountDetails.set(!shouldSave);

    if (shouldSave) {
      const userDetails = this.userDetails();
      const userGuid = this.authService.getUserGuid();

      if (!userDetails || !userGuid) {
        this.toastService.error('Could Not Save');
        return;
      }

      const details: UserDetailsUpdateModel = {
        firstName: userDetails.firstName,
        lastName: userDetails.lastName,
        mobileNumber: userDetails.mobile,
      };

      if (this.newPassword()) {
        details.password = this.newPassword();
        this.newPassword.set('');
      }

      this.userService
        .userUpdate(
          {
            userGuid,
            body: details,
          },
          this.authService.getAuthorisationHeaders(),
        )
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: () => {
            this.toastService.success('Saved');
          },
          error: () => {
            this.toastService.error('Could Not Save');
          },
        });
    }
  }

  /**
   * Opens the create user modal
   */
  addNewUser() {
    const dialogRef = this.dialogService.openDialog(CreateUserComponent, {
      accountGuid: this.accountDetails.guid,
    });

    // Listen to event indicating that new user have been created, and add them to our list view
    dialogRef.componentInstance.userCreated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newUser: UserDetailsModel) => {
        this.accountDetails.users.unshift(newUser);
      });
  }

  patchUserDetails(patch: Partial<UserDetailsModel>) {
    this.userDetails.update((userDetails) =>
      userDetails ? { ...userDetails, ...patch } : userDetails,
    );
  }

  requestEmailChange() {
    const email = this.newEmail().trim();
    const validationError = this.getEmailChangeRequestValidationError(email);

    if (validationError) {
      this.emailChangeError.set(validationError);
      this.emailChangeMessage.set(null);
      return;
    }

    this.submitEmailChangeRequest(email, 'request');
  }

  resendEmailChangeCode() {
    const email = this.pendingEmail();

    if (!email) {
      this.emailChangeStep.set('update');
      this.emailChangeError.set('Enter a new email address.');
      return;
    }

    this.submitEmailChangeRequest(email, 'resend');
  }

  confirmEmailChange() {
    const userGuid = this.authService.getUserGuid();
    const email = this.pendingEmail();
    const code = this.verificationCode().trim();

    if (!userGuid || !email) {
      this.emailChangeError.set('Could not save this email change.');
      return;
    }

    if (!code) {
      this.emailChangeError.set('Enter the verification code.');
      return;
    }

    this.confirmingEmailChange.set(true);
    this.emailChangeError.set(null);
    this.emailChangeMessage.set(null);
    this.userService
      .userEmailChangeConfirmPost(
        {
          userGuid,
          body: {
            email,
            code,
          },
        },
        this.authService.getAuthorisationHeaders(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (tokenModel) => {
          this.authService.setTokens(tokenModel);
          void this.accountStateService
            .refreshAccountsAndReconcileSelected(tokenModel.selectedAccountGuid)
            .then((accountSelection) => {
              if (accountSelection.status === 'no-active-accounts') {
                this.authService.clearAuthState();
                this.emailChangeError.set(
                  'Your account is inactive. Please contact support or your account administrator.',
                );
                this.toastService.error(this.emailChangeError()!);
                void this.router.navigateByUrl('/');
                return;
              }

              this.patchUserDetails({ email, emailVerified: true });
              this.updateAccountUserEmail(userGuid, email);
              this.emailChangeStep.set('saved');
              this.newEmail.set('');
              this.verificationCode.set('');
              this.emailChangeMessage.set(
                'Your email address has been updated.',
              );
              this.toastService.success('Your email address has been updated.');
            })
            .catch((error) => {
              console.error(error);
              this.emailChangeError.set('Could not load your accounts');
              this.toastService.error(this.emailChangeError()!);
            })
            .finally(() => {
              this.confirmingEmailChange.set(false);
            });
        },
        error: (error: HttpErrorResponse) => {
          this.confirmingEmailChange.set(false);
          this.emailChangeError.set(this.getEmailChangeError(error, 'confirm'));
          this.toastService.error(this.emailChangeError()!);
        },
      });
  }

  changePendingEmail() {
    this.newEmail.set(this.pendingEmail());
    this.verificationCode.set('');
    this.emailChangeStep.set('update');
    this.emailChangeError.set(null);
    this.emailChangeMessage.set(null);
  }

  resetEmailChangeFlow() {
    this.emailChangeStep.set('update');
    this.pendingEmail.set('');
    this.newEmail.set('');
    this.verificationCode.set('');
    this.emailChangeError.set(null);
    this.emailChangeMessage.set(null);
  }

  /**
   * Sends an email verification code to the users email
   */
  sendVerificationEmail() {
    const userDetails = this.userDetails();

    if (!userDetails) {
      return;
    }

    this.sendingVerificationEmail.set(true);
    this.emailVerificationService
      .sendVerificationEmail({
        body: {
          userGuid: userDetails.guid,
          emailType: EmailVerificationEmailType.Link,
        },
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.sendingVerificationEmail.set(false);
          this.toastService.success('Verification Email Sent');
        },
        error: () => {
          this.sendingVerificationEmail.set(false);
          this.toastService.error('Could Not Send Verification Email');
        },
      });
  }

  /**
   * Toast informing the user that a feature is coming soon
   * TODO : Remove this when all features implemented
   */
  showComingSoonToast() {
    this.toastService.info('Coming Soon!');
  }

  onThemeChange(event: Event) {
    const theme = (event.target as HTMLInputElement).value;
    this.themeService.setTheme(theme);
  }

  deactivateAccount() {
    if (this.deactivatingAccount()) {
      return;
    }

    if (!this.canDeactivateAccount()) {
      this.deactivationError.set(
        'You do not have permission to deactivate this account.',
      );
      this.toastService.error(this.deactivationError()!);
      return;
    }

    const dialogRef = this.dialogService.openDialog(
      DeactivateAccountDialogComponent,
      {
        accountName: this.accountDetails.displayName,
      },
      560,
    );

    dialogRef
      .afterClosed()
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((currentPassword: string | null | undefined) => {
        if (!currentPassword) {
          return;
        }

        this.submitAccountDeactivation(currentPassword);
      });
  }

  private submitEmailChangeRequest(
    email: string,
    intent: 'request' | 'resend',
  ) {
    const userGuid = this.authService.getUserGuid();

    if (!userGuid) {
      this.emailChangeError.set('Could not request this email change.');
      return;
    }

    if (intent === 'request') {
      this.requestingEmailChange.set(true);
    } else {
      this.resendingEmailChangeCode.set(true);
    }

    this.emailChangeError.set(null);
    this.emailChangeMessage.set(null);
    this.userService
      .userEmailChangeRequestPost(
        {
          userGuid,
          body: {
            email,
          },
        },
        this.authService.getAuthorisationHeaders(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.requestingEmailChange.set(false);
          this.resendingEmailChangeCode.set(false);
          this.pendingEmail.set(email);
          this.verificationCode.set('');
          this.emailChangeStep.set('confirm');
          this.emailChangeMessage.set(
            intent === 'request'
              ? `We sent a verification code to ${email}.`
              : `We sent a new verification code to ${email}.`,
          );
          this.toastService.success('Verification code sent');
        },
        error: (error: HttpErrorResponse) => {
          this.requestingEmailChange.set(false);
          this.resendingEmailChangeCode.set(false);
          this.emailChangeError.set(this.getEmailChangeError(error, 'request'));
          this.toastService.error(this.emailChangeError()!);
        },
      });
  }

  private submitAccountDeactivation(currentPassword: string): void {
    const accountGuid = this.accountDetails.guid;

    this.deactivatingAccount.set(true);
    this.deactivationError.set(null);
    this.accountService
      .accountDelete(
        {
          accountGuid,
          body: {
            currentPassword,
          },
        },
        this.authService.getAuthorisationHeaders(),
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.deactivatingAccount.set(false);
          this.toastService.success('The account has been deactivated.');
          this.accountStateService.clearSelectedAccount(accountGuid);
          void this.refreshAccountsAfterDeactivation(accountGuid);
        },
        error: (error: HttpErrorResponse) => {
          this.deactivatingAccount.set(false);
          this.deactivationError.set(this.getDeactivationError(error));
          this.toastService.error(this.deactivationError()!);

          if (error.status === 404) {
            this.accountStateService.clearSelectedAccount(accountGuid);
            void this.refreshAccountsAfterDeactivation(accountGuid);
          }
        },
      });
  }

  private async refreshAccountsAfterDeactivation(
    deactivatedAccountGuid: string,
  ): Promise<void> {
    try {
      const accountSelection =
        await this.accountStateService.refreshAccountsAndReconcileSelected();

      if (accountSelection.status === 'no-active-accounts') {
        this.authService.clearAuthState();
        this.toastService.info(
          'No active accounts are available for this profile.',
        );
        void this.router.navigateByUrl('/');
        return;
      }

      if (accountSelection.account.guid !== deactivatedAccountGuid) {
        void this.router.navigate(['/admin/home']);
      }
    } catch (error) {
      console.error(error);
      this.deactivationError.set('Could not refresh your accounts');
      this.toastService.error(this.deactivationError()!);
    }
  }

  private getEmailChangeRequestValidationError(email: string) {
    if (!email) {
      return 'Enter a new email address.';
    }

    if (!this.isValidEmail(email)) {
      return 'Enter a valid email address.';
    }

    if (email.toLowerCase() === this.userDetails()?.email?.toLowerCase()) {
      return 'Choose an email address different from your current email.';
    }

    return null;
  }

  private isValidEmail(email: string) {
    return this.emailPattern.test(email);
  }

  private updateAccountUserEmail(userGuid: string, email: string) {
    const accountUser = this.accountDetails.users.find(
      (user) => user.guid === userGuid,
    );

    if (accountUser) {
      accountUser.email = email;
      accountUser.emailVerified = true;
    }
  }

  private getEmailChangeError(
    error: HttpErrorResponse,
    intent: 'request' | 'confirm',
  ) {
    const backendMessage = this.getBackendMessage(error);

    if (error.status === 400 && backendMessage) {
      return backendMessage;
    }

    if (backendMessage && error.status !== 401) {
      return backendMessage;
    }

    switch (error.status) {
      case 400:
        return intent === 'confirm'
          ? 'The verification code could not be accepted.'
          : 'The email address could not be used.';
      case 401:
        return intent === 'confirm'
          ? 'Invalid verification code.'
          : 'Your session has expired. Please sign in again.';
      case 403:
        return intent === 'confirm'
          ? 'Your account is inactive. Please contact support or your account administrator.'
          : 'You do not have permission to update this email address.';
      case 409:
        return 'That email address is already in use.';
      default:
        return 'Could not update your email address. Please try again.';
    }
  }

  private getBackendMessage(error: HttpErrorResponse) {
    const response = error.error as
      | string
      | { message?: string; title?: string; detail?: string }
      | null
      | undefined;

    if (typeof response === 'string') {
      return response;
    }

    return response?.message ?? response?.detail ?? response?.title ?? null;
  }

  private getDeactivationError(error: HttpErrorResponse) {
    switch (error.status) {
      case 400:
        return 'The current password is incorrect.';
      case 401:
        return 'Your session has expired. Please sign in again.';
      case 403:
        return 'You do not have permission to deactivate this account.';
      case 404:
        return 'This account is no longer available.';
      default:
        return 'Could not deactivate this account. Please try again.';
    }
  }
}
